const SCORE_FIELDS = [
  'engine_score',
  'braking_score',
  'tires_score',
  'battery_score',
  'cooling_score',
  'suspension_score',
  'electrical_score',
];

function conditionPenalty(condition) {
  const value = (condition || '').toLowerCase();
  if (value === 'poor') return 35;
  if (value === 'fair') return 15;
  return 0;
}

function textHasAny(text, terms) {
  const hay = (text || '').toLowerCase();
  return terms.some((term) => hay.includes(term.toLowerCase()));
}

function clampScore(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function computeHealthScores(input = {}) {
  const overdueCount = Array.isArray(input.overdue_services)
    ? input.overdue_services.length
    : 0;
  const diagnostic = input.diagnostic_findings || '';
  const accident = input.accident_repair_notes || '';
  const maintenance = input.maintenance_history_notes || '';

  let engine = 100;
  let braking = 100;
  let tires = 100;
  let battery = 100;
  let cooling = 100;
  let suspension = 100;
  let electrical = 100;

  engine = clampScore(engine - Math.min(40, overdueCount * 8));
  if (textHasAny(diagnostic, ['engine', 'oil', 'misfire', 'check engine'])) {
    engine = clampScore(engine - 20);
  }
  if (textHasAny(maintenance, ['overdue', 'missed', 'delayed'])) {
    engine = clampScore(engine - 10);
  }

  braking = clampScore(braking - Math.min(30, overdueCount * 6));
  if (textHasAny(`${accident} ${diagnostic}`, ['brake', 'braking', 'abs'])) {
    braking = clampScore(braking - 15);
  }

  tires = clampScore(tires - conditionPenalty(input.tire_condition));
  if (textHasAny(diagnostic, ['tire', 'alignment', 'rotation', 'tread'])) {
    tires = clampScore(tires - 10);
  }

  battery = clampScore(battery - conditionPenalty(input.battery_condition));
  if (textHasAny(diagnostic, ['battery', 'alternator', 'start'])) {
    battery = clampScore(battery - 15);
  }

  cooling = clampScore(cooling - Math.min(25, overdueCount * 5));
  if (textHasAny(diagnostic, ['coolant', 'overheat', 'temperature', 'radiator'])) {
    cooling = clampScore(cooling - 20);
  }

  suspension = clampScore(suspension - 10);
  if (textHasAny(`${accident} ${diagnostic}`, ['suspension', 'shock', 'strut', 'alignment'])) {
    suspension = clampScore(suspension - 20);
  }

  electrical = clampScore(electrical - 10);
  if (textHasAny(diagnostic, ['warning light', 'electrical', 'sensor', 'ecu', 'light'])) {
    electrical = clampScore(electrical - 20);
  }
  electrical = clampScore(
    electrical - Math.floor(conditionPenalty(input.battery_condition) / 2),
  );

  const overall = clampScore(
    (engine + braking + tires + battery + cooling + suspension + electrical) / 7,
  );

  return {
    overall_score: overall,
    engine_score: engine,
    braking_score: braking,
    tires_score: tires,
    battery_score: battery,
    cooling_score: cooling,
    suspension_score: suspension,
    electrical_score: electrical,
  };
}

export function applyScoreOverrides(computed, draft, overrides = {}) {
  const next = { ...computed };
  for (const field of SCORE_FIELDS) {
    if (overrides[field]) {
      next[field] = clampScore(Number(draft[field] ?? computed[field] ?? 0));
    }
  }
  if (overrides.overall_score) {
    next.overall_score = clampScore(Number(draft.overall_score ?? computed.overall_score));
  } else {
    next.overall_score = clampScore(
      (
        next.engine_score
        + next.braking_score
        + next.tires_score
        + next.battery_score
        + next.cooling_score
        + next.suspension_score
        + next.electrical_score
      ) / 7,
    );
  }
  return next;
}

export function scoreBand(score) {
  if (score >= 80) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
}

export { SCORE_FIELDS };
